import { QuotationActionMenu } from "Components/MenuActionButton/QuotationMenu";
import { formatDate } from "helpers/functions/formatDate";
import { formatLabel } from "helpers/functions/formatLabel";
import { useHandleDownloadPDF } from "helpers/functions/handleDownloadPDF";
import { useHandleDownloadPDF2 } from "helpers/functions/handleDownloadPDF2";
import { Clock, FileText } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { changeQuotationStatusById, fetchQuotationLog } from "slices/Quotation/thunk";
import QuotationDetailsModal from "./QuotationDetailsModal";

interface LatestQuotationsProps {
  className?: string;
}

export function LatestQuotations({ className = "" }: LatestQuotationsProps) {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [quotations, setQuotations] = useState([]);
  const [error, setError] = useState<string | null>(null);
  const [showQuotationModal, setShowQuotationModal] = useState(false);
  const [selectedQuotationId, setSelectedQuotationId] = useState<string | null>(
    null
  );

  // Use PDF download hooks
  const handleDownloadPDF = useHandleDownloadPDF();
  const handleDownloadPDF2 = useHandleDownloadPDF2();

  // const user = JSON.parse(localStorage.getItem("authUser") || "{}");
  // const isAdmin = user?.user_type === "Admin";

  // Fetch latest 10 quotations
  useEffect(() => {
    const fetchLatestQuotations = async () => {
      try {
        setLoading(true);
        setError(null);

        const result = await dispatch(
          fetchQuotationLog({
            page: 1,
            pageSize: 10,
            sort: "created_at",
            order: "desc",
            search: "",
            filters: [],
          })
        ).unwrap();

        setQuotations(result.data || []);
      } catch (err: any) {
        console.error("Failed to fetch latest quotations:", err);
        setError("Failed to load latest quotations");
      } finally {
        setLoading(false);
      }
    };

    fetchLatestQuotations();
  }, [dispatch]);

  // const handleDelete = async (id: any) => {
  //   if (!isAdmin) {
  //     toast.error("Only administrators can delete quotations");
  //     return;
  //   }

  //   const result = await Swal.fire({
  //     title: "Are you sure?",
  //     text: "Are You Sure You Want To Delete Quotation!",
  //     icon: "warning",
  //     showCancelButton: true,
  //     confirmButtonColor: "#d33",
  //     cancelButtonColor: "#3085d6",
  //     confirmButtonText: "Yes, delete it!",
  //     width: "350px",
  //     customClass: {
  //       popup: "swal2-sm-popup",
  //     },
  //   });

  //   if (!result.isConfirmed) return;

  //   try {
  //     await dispatch(deleteQuotationById(id)).unwrap();
  //     toast.success("Quotation deleted successfully!!");

  //     // Refresh the list
  //     const result = await dispatch(
  //       fetchQuotationLog({
  //         page: 1,
  //         pageSize: 10,
  //         sort: "created_at",
  //         order: "desc",
  //         search: "",
  //         filters: [],
  //       })
  //     ).unwrap();
  //     setQuotations(result.data || []);
  //   } catch (err: any) {
  //     toast.error("Failed to Delete Quotation");
  //   }
  // };

  const handleStatusChange = async (id: any, newStatus: any) => {
    try {
      await dispatch(
        changeQuotationStatusById({ id: id, payload: newStatus })
      ).unwrap();
      toast.success("Status Changed Successfully!!");

      // Refresh the list
      const result = await dispatch(
        fetchQuotationLog({
          page: 1,
          pageSize: 10,
          sort: "created_at",
          order: "desc",
          search: "",
          filters: [],
        })
      ).unwrap();
      setQuotations(result.data || []);
    } catch (err: any) {
      console.log(err);
      toast.error("Failed to Change Quotation Status");
    }
  };

  const handleViewQuotation = useCallback((id: string) => {
    setSelectedQuotationId(id);
    setShowQuotationModal(true);
  }, []);

  const handleCloseQuotationModal = useCallback(() => {
    setShowQuotationModal(false);
    setSelectedQuotationId(null);
  }, []);

  if (loading) {
    return (
      <div className={`card h-100 ${className}`}>
        <div className="card-body">
          <h5 className="card-title mb-4">Latest Quotations</h5>
          <div className="d-flex align-items-center justify-content-center py-5">
            <Spinner
              animation="border"
              variant="primary"
              size="sm"
              className="me-2"
            />
            <span className="text-muted">Loading latest quotations...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`card h-100 ${className}`}>
        <div className="card-body">
          <h5 className="card-title mb-4">Latest Quotations</h5>
          <div className="text-center py-4">
            <div className="text-danger mb-2">
              <FileText size={48} />
            </div>
            <p className="text-muted">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`card h-100 ${className}`}>
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h5 className="card-title mb-0">Latest Quotations</h5>
          {/* <button
            className="btn btn-outline-primary btn-sm"
            onClick={() => navigate("/quotation")}
          >
            <Eye size={16} className="me-1" />
            View All
          </button> */}
        </div>

        {quotations.length === 0 ? (
          <div className="text-center py-4">
            <div className="text-muted mb-2">
              <FileText size={48} />
            </div>
            <p className="text-muted">No quotations found</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Quotation number</th>
                  <th>Customer</th>
                  <th>Quotation Date</th>
                  <th>Status</th>
                  <th>Mode</th>
                  <th>Amount</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {quotations.map((quotation: any, index: number) => (
                  <tr key={quotation.quotation_id || index}>
                    <td>
                      <div className="fw-semibold text-primary">
                        {quotation.quotation_auto_id || "-"}
                      </div>
                    </td>
                    <td>
                      <div>
                        <div className="fw-semibold">
                          {quotation.customer_details?.customer_name || "-"}
                        </div>
                        <small className="text-muted">
                          {quotation.customer_details?.mobile_no || "-"}
                        </small>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <Clock size={14} className="text-muted" />
                        <span className="text-muted small">
                          {formatDate(quotation.quotation_date)}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span
                        className={`badge text-white ${
                          quotation.current_status === "Draft"
                            ? "bg-primary"
                            : quotation.current_status === "Sent"
                            ? "bg-warning"
                            : quotation.current_status === "Won"
                            ? "bg-success"
                            : quotation.current_status === "Lost"
                            ? "bg-danger"
                            : quotation.current_status === "PI Generated"
                            ? "bg-success"
                            : "bg-info"
                        } text-dark`}
                      >
                        {quotation.current_status || "-"}
                      </span>

                      {/* <StatusCell
                        row={{ original: quotation }}
                        handleStatusChange={handleStatusChange}
                        canChangeStatus={false}
                      /> */}
                    </td>
                    <td>
                      <span className="badge bg-light text-dark">
                        {formatLabel(quotation.quotation_creation_type) || "-"}
                      </span>
                    </td>
                    <td>
                      <span className="fw-semibold text-primary">
                      ₹ {Number(quotation.quotation_total_amount || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                      </span>
                    </td>
                    <td>
                      <div onClick={(e) => e.stopPropagation()}>
                        <QuotationActionMenu
                          row={{ original: quotation }}
                          // handleDelete={handleDelete}
                          handleDownloadPDF={handleDownloadPDF}
                          handleDownloadPDF2={handleDownloadPDF2}
                          handleViewModal={handleViewQuotation}
                          canChange={false}
                          // canDelete={isAdmin}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Quotation Details Modal */}
      <QuotationDetailsModal
        show={showQuotationModal}
        onHide={handleCloseQuotationModal}
        quotationId={selectedQuotationId}
      />
    </div>
  );
}
